import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/Layout";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { duration, money, pct, title } from "@/lib/format";

const perf = (label) => [
  { key: "_id", label, render: (r) => <span className="font-medium text-slate-800">{title(r._id) || "—"}</span> },
  { key: "calls", label: "Calls" },
  { key: "answered", label: "Answered" },
  { key: "rate", label: "Answer rate", render: (r) => pct(r.calls ? (r.answered / r.calls) * 100 : 0) },
  { key: "interested", label: "Interested" },
  { key: "avg_interest", label: "Avg score", render: (r) => (r.avg_interest || 0).toFixed(1) },
  { key: "avg_duration", label: "Avg duration", render: (r) => duration(r.avg_duration) },
  { key: "cost", label: "Cost", render: (r) => money(r.cost) },
];

export default function Analytics() {
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const params = Object.fromEntries(Object.entries(range).filter(([, v]) => v));
  const { data, isLoading } = useQuery({ queryKey: ["analytics", params], queryFn: () => get("/analytics", params) });

  return (
    <>
      <PageHeader
        testId="analytics-page"
        title="Analytics"
        subtitle="Performance by language, country, agent, campaign and hour of day"
        actions={
          <div className="flex gap-2">
            <Input data-testid="analytics-date-from" type="date" value={range.date_from} onChange={(e) => setRange((r) => ({ ...r, date_from: e.target.value }))} className="h-9" />
            <Input data-testid="analytics-date-to" type="date" value={range.date_to} onChange={(e) => setRange((r) => ({ ...r, date_to: e.target.value }))} className="h-9" />
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="Answer rate by hour of day" testId="analytics-hourly">
          <div className="h-64 px-2 py-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={(data?.hourly || []).map((h) => ({
                hour: `${h._id}:00`, calls: h.calls, answered: h.answered,
              }))}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="hour" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="calls" stroke="#2563EB" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="answered" stroke="#059669" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title="Outcomes" testId="analytics-outcomes">
          <div className="h-64 px-2 py-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(data?.by_outcome || []).map((o) => ({ name: title(o._id), count: o.count }))} layout="vertical">
                <CartesianGrid stroke="#E2E8F0" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Bar dataKey="count" fill="#2563EB" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      <Section title="Performance by language" testId="analytics-language">
        <DataTable testId="analytics-language-table" loading={isLoading} columns={perf("Language")} rows={(data?.by_language || []).map((r, i) => ({ ...r, id: i }))} empty={{ title: "No data" }} />
      </Section>

      <Section title="Performance by campaign" testId="analytics-campaign">
        <DataTable testId="analytics-campaign-table" loading={isLoading} columns={perf("Campaign")} rows={(data?.by_campaign || []).map((r, i) => ({ ...r, id: i }))} empty={{ title: "No data" }} />
      </Section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="Performance by country" testId="analytics-country">
          <DataTable testId="analytics-country-table" loading={isLoading} columns={perf("Country")} rows={(data?.by_country || []).map((r, i) => ({ ...r, id: i }))} empty={{ title: "No data" }} />
        </Section>
        <Section title="Performance by agent" testId="analytics-agent">
          <DataTable testId="analytics-agent-table" loading={isLoading} columns={perf("Agent")} rows={(data?.by_agent || []).map((r, i) => ({ ...r, id: i }))} empty={{ title: "No data" }} />
        </Section>
      </div>

      <Section title="Average quality score by campaign" testId="analytics-quality">
        <DataTable
          testId="analytics-quality-table"
          loading={isLoading}
          columns={[
            { key: "_id", label: "Campaign" },
            { key: "calls", label: "Scored calls" },
            { key: "avg_quality", label: "Avg quality", render: (r) => `${Math.round(r.avg_quality || 0)}/100` },
            {
              key: "band",
              label: "Band",
              render: (r) => <StatusBadge value={r.avg_quality >= 80 ? "good" : r.avg_quality >= 60 ? "needs_improvement" : "bad"} />,
            },
          ]}
          rows={(data?.quality || []).map((r, i) => ({ ...r, id: i }))}
          empty={{ title: "No scored calls yet" }}
        />
      </Section>
    </>
  );
}
